// Truyền Âm (UI_SPEC §17): sổ ghi của game. Lưu KHÓA + tham số, không lưu câu đã dịch.
import { signal, computed, batch } from '@preact/signals';
import { registerSlice } from '../registry';
import { now } from '../clock';
import { isPlainObject } from '@save/types';

export const LOG_CHANNELS = ['cultivation', 'battle', 'world', 'fate', 'system'] as const;
export type LogChannel = (typeof LOG_CHANNELS)[number];

/** Gộp dòng cùng khóa trong cửa sổ này thành một dòng ×n. */
export const LOG_MERGE_MS = 60_000;
/** Trần mỗi kênh; cũ nhất bị cắt. */
export const LOG_CHANNEL_CAP = 200;

export interface LogEntry {
  channel: LogChannel;
  key: string;
  params?: Record<string, string | number>;
  at: number;
  count: number;
}

interface LogState {
  entries: LogEntry[];
  /** Mốc đọc gần nhất theo kênh. */
  readAt: Partial<Record<LogChannel, number>>;
}

const _log = signal<LogState>({ entries: [], readAt: {} });
export const logEntries = computed(() => _log.value.entries);

export const unreadByChannel = computed(() => {
  const out = Object.fromEntries(LOG_CHANNELS.map((c) => [c, 0])) as Record<LogChannel, number>;
  const { entries, readAt } = _log.value;
  for (const e of entries) if (e.at > (readAt[e.channel] ?? 0)) out[e.channel]++;
  return out;
});
export const unreadTotal = computed(() => Object.values(unreadByChannel.value).reduce((a, b) => a + b, 0));

function sameParams(a: LogEntry['params'], b: LogEntry['params']): boolean {
  return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
}

export function logLine(
  channel: LogChannel,
  key: string,
  params?: LogEntry['params'],
  at: number = now(),
): void {
  const { entries, readAt } = _log.value;
  const last = [...entries].reverse().find((e) => e.channel === channel);
  let next: LogEntry[];
  if (
    last &&
    last.key === key &&
    sameParams(last.params, params) &&
    at >= last.at &&
    at - last.at <= LOG_MERGE_MS
  ) {
    next = entries.map((e) => (e === last ? { ...e, at, count: e.count + 1 } : e));
  } else {
    next = [...entries, { channel, key, ...(params ? { params } : {}), at, count: 1 }];
    const inChannel = next.filter((e) => e.channel === channel);
    if (inChannel.length > LOG_CHANNEL_CAP) {
      const drop = new Set(inChannel.slice(0, inChannel.length - LOG_CHANNEL_CAP));
      next = next.filter((e) => !drop.has(e));
    }
  }
  _log.value = { entries: next, readAt };
}

export function markChannelRead(channel: LogChannel, at: number = now()): void {
  batch(() => {
    _log.value = { ..._log.value, readAt: { ..._log.value.readAt, [channel]: at } };
  });
}

function isChannel(v: unknown): v is LogChannel {
  return typeof v === 'string' && (LOG_CHANNELS as readonly string[]).includes(v);
}

registerSlice<LogState>({
  key: 'log',
  scope: 'core',
  serialize: () => _log.value,
  hydrate: (d) => {
    const st: LogState = { entries: [], readAt: {} };
    if (isPlainObject(d)) {
      if (Array.isArray(d.entries)) {
        for (const e of d.entries as unknown[]) {
          if (
            !isPlainObject(e) ||
            !isChannel(e.channel) ||
            typeof e.key !== 'string' ||
            typeof e.at !== 'number'
          )
            continue;
          st.entries.push({
            channel: e.channel,
            key: e.key,
            at: e.at,
            count: typeof e.count === 'number' ? e.count : 1,
            ...(isPlainObject(e.params) ? { params: e.params as LogEntry['params'] } : {}),
          });
        }
      }
      if (isPlainObject(d.readAt))
        for (const c of LOG_CHANNELS) {
          const v = d.readAt[c];
          if (typeof v === 'number') st.readAt[c] = v;
        }
    }
    _log.value = st;
  },
});
